import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollView as ScrollViewNative } from "react-native";
import { generatePath, useLocation, useNavigate, useParams } from "react-router";
import { v4 as uuid } from "uuid";
import { Spinner, View } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { QueryStatus } from "@lookiero/messaging-react";
import { Layout } from "@lookiero/sty-psp-ui";
import { Product } from "../../../../domain/entities/product/Product";
import { ProductPreview } from "../../../../domain/projections/ProductPreview";
import { VariantWithColorAndImage } from "../../../../domain/projections/VariantWithColorAndImage";
import { useViewIsCategoriesOfferingEnabled } from "../../../projection/offering/react/useViewIsCategoriesOfferingEnabled";
import { useViewIsCompleteYourLookOfferingEnabled } from "../../../projection/offering/react/useViewIsCompleteYourLookOfferingEnabled";
import { useAddProductToBasket } from "../../behaviors/useAddProductToBasket";
import { useAddProductToStockNotification } from "../../behaviors/useAddProductToStockNotification";
import { useAddProductToWishlist } from "../../behaviors/useAddProductToWishlist";
import { useGetBenefitMediaUrlByVariantId } from "../../behaviors/useGetBenefitMediaUrlByVariantId";
import { useGetExperiments } from "../../behaviors/useGetExperiments";
import { useGetOrder } from "../../behaviors/useGetOrder";
import { useGetProductDetails } from "../../behaviors/useGetProductDetails";
import { useHardwareBackPress } from "../../behaviors/useHardwareBackPress";
import { useRemoveVariantFromWishlist } from "../../behaviors/useRemoveVariantFromWishlist";
import { useShowDeliveryAndReturnProductPage } from "../../behaviors/useShowDeliveryAndReturnProductPage";
import { ProductDetail as ProductDetailTemplate } from "../../components/templates/productDetail/ProductDetail";
import { useMouseFlow } from "../../hooks/useMouseFlow";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { useToast } from "../../hooks/useToast";
import { DOMAIN, I18nMessages } from "../../i18n/i18n";
import { Routes } from "../../routing/routes";
import { Offering } from "../../shared/types";
import { STORAGE_KEY_PRODUCT_DETAIL_SCROLL } from "../../shared/util/storageKeys";
import { List, TrackingPage, TrackingView } from "../../tracking/tracking";
import { useTrackBasketItemAdded } from "../../tracking/useTrackBasketItemAdded";
import { useTrackItemDetailDisplayed } from "../../tracking/useTrackItemDetailDisplayed";
import { useTrackItemDetailSoldOut } from "../../tracking/useTrackItemDetailSoldOut";
import { useTrackItemSelected } from "../../tracking/useTrackItemSelected";
import { useTrackNotifyStockButtonClicked } from "../../tracking/useTrackNotifyStockButtonClicked";
import { useTrackPageView } from "../../tracking/useTrackPageView";
import { useTrackShopButtonClicked } from "../../tracking/useTrackShopButtonClicked";
import { useTrackShowNotifyStockButton } from "../../tracking/useTrackShowNotifyStockButton";
import { useTrackWhishlistItemAdded } from "../../tracking/useTrackWhishlistItemAdded";
import { DEFAULT_SCROLL_VALUE, MOUSE_FLOW_EVENT, SIZE_NOT_AVAILABLE } from "./ProductDetail.definition";
import { style } from "./ProductDetail.styles";

interface ProductDetailProps {
  readonly layout: Layout;
}

const ProductDetail: FC<ProductDetailProps> = ({ layout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {
    variantId: variantIdFromRoute = "",
    lookId: lookIdFromRoute = "",
    categoryId: categoryIdFromRoute = "",
  } = params;

  const {
    session: { auth, user },
    store,
    basePath,
    tradename,
    apiUrl,
  } = useStaticInfo();

  const [isCompleteYourLookOfferingEnabled, isCompleteYourLookOfferingEnabledStatus] =
    useViewIsCompleteYourLookOfferingEnabled();
  const [isCategoriesOfferingEnabled, isCategoriesOfferingEnabledStatus] = useViewIsCategoriesOfferingEnabled();

  const productStockOutNotificationSuccessText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_STOCK_OUT_NOTIFICATION_SUCCESS,
  });
  const productStockOutNotificationErrorText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_STOCK_OUT_NOTIFICATION_ERROR,
  });
  const whishlistNotificationItemAddedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_NOTIFICATION_ITEM_ADDED,
  });
  const whishlistNotificationItemRemovedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_NOTIFICATION_ITEM_REMOVED,
  });
  const productSizeSelectorItemAddedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_SIZE_SELECTOR_ITEM_ADDED,
  });
  const emptyOptionText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.EMPTY_OPTION,
  });

  const trackingCommonParams = useMemo(
    () => ({
      country: user.country,
      segment: user.segment,
      userId: user.id,
      tradename,
    }),
    [tradename, user.country, user.id, user.segment],
  );
  useTrackPageView({
    ...trackingCommonParams,
    page: TrackingPage.PRODUCT_DETAIL,
    view: TrackingView.LOOK_IN_PRODUCT_DETAIL,
  });
  const trackBasketItemAdded = useTrackBasketItemAdded(trackingCommonParams);
  const trackShopButtonClicked = useTrackShopButtonClicked(trackingCommonParams);
  const trackItemDetailDisplayed = useTrackItemDetailDisplayed(trackingCommonParams);
  const trackItemDetailSoldOut = useTrackItemDetailSoldOut(trackingCommonParams);
  const trackItemSelected = useTrackItemSelected(trackingCommonParams);
  const trackNotifyStockButtonClicked = useTrackNotifyStockButtonClicked(trackingCommonParams);
  const trackShowNotifyStockButton = useTrackShowNotifyStockButton(trackingCommonParams);
  const trackWhishlistItemAdded = useTrackWhishlistItemAdded(trackingCommonParams);

  const toast = useToast();
  const scrollViewRef = useRef<ScrollViewNative>(null);
  const scrollValueRef = useRef<number>(DEFAULT_SCROLL_VALUE);

  const isRecommended = Boolean(lookIdFromRoute) || Boolean(categoryIdFromRoute);
  const [selectedSize, setSelectedSize] = useState<string>(variantIdFromRoute);
  const {
    productDetails,
    isLoading: isLoadingProductDetail,
    refetchProductDetail,
  } = useGetProductDetails(variantIdFromRoute, isRecommended);
  const addProductToWishlist = useAddProductToWishlist();
  const removeVariantFromWishlist = useRemoveVariantFromWishlist();
  const addProductToBasket = useAddProductToBasket();
  const { order, refetchOrder } = useGetOrder();
  const { fireMouseFlowEvent } = useMouseFlow();
  const { experiments } = useGetExperiments();
  const { showDeliveryAndReturnProductPage } = useShowDeliveryAndReturnProductPage();

  const benefitsMediaUrl = useGetBenefitMediaUrlByVariantId(variantIdFromRoute);

  useEffect(() => {
    if (variantIdFromRoute && productDetails.sizeVariants) {
      const variantId = productDetails.sizeVariants.find(
        (sizeVariant) => sizeVariant.size === productDetails.size,
      )?.variantId;
      setSelectedSize(variantId || variantIdFromRoute);
    }
  }, [productDetails, productDetails.size, productDetails.sizeVariants, variantIdFromRoute]);

  const saveScrollValueInStore = useCallback(
    async (value: number) => {
      const scrollValue = value < 0 ? 0 : value;
      const currentScrollValue = await store.get(STORAGE_KEY_PRODUCT_DETAIL_SCROLL);

      await store.set(STORAGE_KEY_PRODUCT_DETAIL_SCROLL, {
        ...(currentScrollValue || {}),
        [variantIdFromRoute]: scrollValue,
      });
    },
    [store, variantIdFromRoute],
  );

  const resetScrollForVariant = useCallback(async () => {
    const currentScrollValue = await store.get(STORAGE_KEY_PRODUCT_DETAIL_SCROLL);
    const newScrollValue: Record<string, number> = { ...(currentScrollValue || {}) };
    delete newScrollValue[variantIdFromRoute];

    await store.set(STORAGE_KEY_PRODUCT_DETAIL_SCROLL, newScrollValue);
  }, [store, variantIdFromRoute]);

  const handleOnScroll = useCallback((coords: { X: number; Y: number }) => {
    scrollValueRef.current = coords.Y;
  }, []);

  const onToStockNotificationSuccess = useCallback(() => {
    toast.showSuccess({ message: productStockOutNotificationSuccessText });

    trackNotifyStockButtonClicked({
      variantId: productDetails.variantId,
    });
  }, [toast, productStockOutNotificationSuccessText, trackNotifyStockButtonClicked, productDetails.variantId]);

  const onToStockNotificationError = useCallback(() => {
    toast.showError({
      message: productStockOutNotificationErrorText,
    });
  }, [toast, productStockOutNotificationErrorText]);

  const addProductToStockNotification = useAddProductToStockNotification({
    onSuccess: onToStockNotificationSuccess,
    onError: onToStockNotificationError,
  });

  const handleOnChangeColor = useCallback(
    (variantColor: VariantWithColorAndImage) => {
      const item = {
        title: productDetails.title,
        id: variantColor.id,
        price: productDetails.price,
        brand: productDetails.brand,
      } as ProductPreview;

      trackItemSelected({
        list: List.COLOR_PICKER,
        productPreview: item,
      });

      navigate(generatePath(`${basePath}/${Routes.PRODUCT_DETAIL}`, { variantId: variantColor.id }), {
        replace: true,
        state: { innerNavigation: true },
      });
    },
    [productDetails.title, productDetails.price, productDetails.brand, trackItemSelected, navigate, basePath],
  );

  const handleAddToWishlist = useCallback(
    async (product: Product) => {
      const { variantId } = product;

      addProductToWishlist(variantId, () => {
        toast.showSuccess({ message: whishlistNotificationItemAddedText });

        trackWhishlistItemAdded({
          product,
        });

        refetchProductDetail();
      });
    },
    [addProductToWishlist, toast, whishlistNotificationItemAddedText, trackWhishlistItemAdded, refetchProductDetail],
  );

  const handleRemoveFromWishlist = useCallback(
    async (product: Product) => {
      const { variantId } = product;
      removeVariantFromWishlist(variantId, () => {
        toast.showSuccess({ message: whishlistNotificationItemRemovedText });
        refetchProductDetail();
      });
    },
    [removeVariantFromWishlist, toast, whishlistNotificationItemRemovedText, refetchProductDetail],
  );

  const handleAddToBasket = useCallback(() => {
    const { orderId, serviceId } = order;
    const { itemId, variantId } = productDetails;

    const payload = {
      auth,
      itemId: itemId || uuid(),
      orderId,
      variantId,
      serviceId,
      apiUrl,
    };

    addProductToBasket(payload, () => {
      refetchOrder();

      trackBasketItemAdded({
        product: productDetails,
        currency: order.currency,
      });

      toast.showSuccess({ message: productSizeSelectorItemAddedText });
    });
  }, [
    order,
    productDetails,
    auth,
    apiUrl,
    addProductToBasket,
    refetchOrder,
    trackBasketItemAdded,
    toast,
    productSizeSelectorItemAddedText,
  ]);

  const handleOnPressOtherLookProduct = useCallback(() => {
    saveScrollValueInStore(scrollValueRef.current);
  }, [saveScrollValueInStore]);

  const openSizeSurvey = useCallback(() => {
    fireMouseFlowEvent(MOUSE_FLOW_EVENT.NAME, MOUSE_FLOW_EVENT.ID);
  }, [fireMouseFlowEvent]);

  const fireProductDetailLoadEvent = useCallback(() => {
    trackItemDetailDisplayed({
      item: productDetails,
      lookId: lookIdFromRoute,
      lastUnits: (productDetails.lastUnits && productDetails.isAvailable) || false,
      experiments,
    });
  }, [trackItemDetailDisplayed, productDetails, lookIdFromRoute, experiments]);

  const fireGarmentSoldOutEvent = useCallback(
    (isProductAvailable: boolean) => {
      const productNotAvailable = isProductAvailable ? [] : [productDetails];

      trackItemDetailSoldOut({
        products: productNotAvailable,
        variantId: productDetails.variantId,
        stockAvailable: isProductAvailable,
      });
    },
    [productDetails, trackItemDetailSoldOut],
  );

  const fireShowNotifyStockButton = useCallback(() => {
    trackShowNotifyStockButton({
      product: productDetails,
    });
  }, [productDetails, trackShowNotifyStockButton]);

  useEffect(() => {
    if (productDetails && !isLoadingProductDetail) {
      fireProductDetailLoadEvent();

      if (productDetails.size) {
        fireGarmentSoldOutEvent(Boolean(productDetails.isAvailable));

        if (!productDetails.isAvailable) {
          fireShowNotifyStockButton();
        }
      }
    }
  }, [
    fireProductDetailLoadEvent,
    fireGarmentSoldOutEvent,
    fireShowNotifyStockButton,
    isLoadingProductDetail,
    productDetails,
  ]);

  const handleOnLayout = useCallback(async () => {
    const storedScroll = (await store.get(STORAGE_KEY_PRODUCT_DETAIL_SCROLL)) as Record<string, number>;
    const variantStoredScroll = storedScroll ? storedScroll[variantIdFromRoute] : undefined;

    if (Number(variantStoredScroll) >= 0 && scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ x: 0, y: Number(variantStoredScroll), animated: false });
    }
  }, [store, variantIdFromRoute]);

  const handleOnChangeProductSize = useCallback(
    (variantId: string) => {
      if (variantId !== SIZE_NOT_AVAILABLE && emptyOptionText !== variantId && variantId !== variantIdFromRoute) {
        const product = {
          title: productDetails.title,
          id: variantId,
          price: productDetails.price,
          brand: productDetails.brand,
        } as ProductPreview;

        trackItemSelected({
          list: List.SIZE_PICKER,
          productPreview: product,
        });

        navigate(generatePath(`${basePath}/${Routes.PRODUCT_DETAIL}`, { variantId }), {
          replace: true,
          state: { innerNavigation: true },
        });
      }
      setSelectedSize(variantId);
      openSizeSurvey();
    },
    [
      emptyOptionText,
      variantIdFromRoute,
      openSizeSurvey,
      productDetails.title,
      productDetails.price,
      productDetails.brand,
      trackItemSelected,
      navigate,
      basePath,
    ],
  );

  const scrollToBenefits = useCallback(
    (coordinateY: number) => {
      let userType = "";

      if (isCategoriesOfferingEnabled && isCompleteYourLookOfferingEnabled) userType = "CYLCAT";
      if (isCategoriesOfferingEnabled) userType = "CAT";

      trackShopButtonClicked({
        buttonId: "benefit_show_look",
        userType,
      });

      return scrollViewRef.current?.scrollTo({ y: coordinateY, animated: true });
    },
    [isCategoriesOfferingEnabled, isCompleteYourLookOfferingEnabled, trackShopButtonClicked],
  );

  // Block body (not an expression) so the callback's inferred return type is `void`, not
  // react-router 7's `void | Promise<void>` `NavigateFunction` return — `useHardwareBackPress`'s
  // `HandleBackFunction` is a `boolean | void | undefined` union, which doesn't get the implicit
  // "void accepts anything" leniency TypeScript gives to a plain `void` return type.
  const onBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useHardwareBackPress(onBack);

  const handleOnPressWishlist = useCallback(() => {
    if (productDetails.wishlisted) {
      handleRemoveFromWishlist(productDetails);
    } else {
      handleAddToWishlist(productDetails);
    }
  }, [productDetails, handleRemoveFromWishlist, handleAddToWishlist]);

  const scrollToWatchDistributions = useCallback(
    (coordinateY: number) => scrollViewRef.current?.scrollTo({ y: coordinateY, animated: true }),
    [scrollViewRef],
  );

  const handleOnPressProduct = useCallback(
    () => saveScrollValueInStore(scrollValueRef.current),
    [saveScrollValueInStore],
  );

  const handleOnBack = useCallback(() => {
    resetScrollForVariant();

    if (location.state?.innerNavigation) {
      navigate(-1);
    } else {
      const { lookId, categoryId } = params;
      const offeringType = lookId ? Offering.COMPLETE_YOUR_LOOK : categoryId ? Offering.CATEGORIES : null;

      if (offeringType === Offering.COMPLETE_YOUR_LOOK) {
        navigate(`${basePath}/${Routes.COMPLETE_YOUR_LOOK}`);
      } else if (offeringType === Offering.CATEGORIES) {
        navigate(`${basePath}/${Routes.CATEGORIES}/${categoryId}`);
      } else {
        navigate(`${basePath}/${Routes.HOME}`);
      }
    }
  }, [basePath, location.state?.innerNavigation, navigate, params, resetScrollForVariant]);

  const handleOnPressBasket = useCallback(() => navigate(`${basePath}/${Routes.CART}`), [basePath, navigate]);

  const goToWishlist = useCallback(() => navigate(`${basePath}/${Routes.WISHLIST}`), [basePath, navigate]);

  const dependenciesLoadedStatuses = [QueryStatus.ERROR, QueryStatus.SUCCESS];
  const dependenciesLoaded =
    !isLoadingProductDetail &&
    dependenciesLoadedStatuses.includes(isCompleteYourLookOfferingEnabledStatus) &&
    dependenciesLoadedStatuses.includes(isCategoriesOfferingEnabledStatus);

  if (!dependenciesLoaded) {
    return (
      <View style={style.spinnerWrapper}>
        <Spinner />
      </View>
    );
  }

  return (
    <ProductDetailTemplate
      addProductToStockNotification={addProductToStockNotification}
      benefitsMediaUrl={benefitsMediaUrl || ""}
      goToWishlist={goToWishlist}
      hasNotification={order.items.length > 0}
      layout={layout}
      lookIdFromRoute={lookIdFromRoute}
      product={productDetails}
      scrollToBenefits={scrollToBenefits}
      scrollToWatchDistributions={scrollToWatchDistributions}
      scrollViewRef={scrollViewRef}
      selectedSize={selectedSize}
      showDeliveryAndReturn={showDeliveryAndReturnProductPage}
      variantIdFromRoute={variantIdFromRoute}
      onBack={handleOnBack}
      onChangeColor={handleOnChangeColor}
      onChangeProductSize={handleOnChangeProductSize}
      onLayout={handleOnLayout}
      onPressAddToBasket={handleAddToBasket}
      onPressBasket={handleOnPressBasket}
      onPressOtherLookProduct={handleOnPressOtherLookProduct}
      onPressProduct={handleOnPressProduct}
      onPressWishlist={handleOnPressWishlist}
      onScroll={handleOnScroll}
    />
  );
};

export { ProductDetail };
